import { Configuration, PopupRequest } from '@azure/msal-browser';

// MSAL configuration for Azure AD authentication
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID || 'common'}`,
    redirectUri: import.meta.env.VITE_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri: import.meta.env.VITE_POST_LOGOUT_REDIRECT_URI || window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage', // Use sessionStorage for security
    storeAuthStateInCookie: false, // Set to true for IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case 0: // LogLevel.Error
            console.error(message);
            return;
          case 1: // LogLevel.Warning
            console.warn(message);
            return;
          case 2: // LogLevel.Info
            console.info(message);
            return;
          case 3: // LogLevel.Verbose
            console.debug(message);
            return;
        }
      },
    },
  },
};

// Scopes for API access
export const loginRequest: PopupRequest = {
  scopes: ['User.Read', 'openid', 'profile'],
};

// API scopes for backend calls
export const apiRequest = {
  scopes: [import.meta.env.VITE_API_SCOPE || 'api://hitl-validation/access_as_user'],
};

// Role-based access control configuration
export const ROLES = {
  PLATFORM_ADMIN: 'REA.PlatformAdmin',
  TENANT_ADMIN: 'REA.TenantAdmin',
  ANALYST: 'REA.Analyst',
} as const;

export type UserRole = typeof ROLES[keyof typeof ROLES];

/** Map Azure AD role claims to backend role strings */
export function mapToBackendRole(userRoles: string[]): 'PlatformAdmin' | 'TenantAdmin' | 'Analyst' {
  if (userRoles.includes(ROLES.PLATFORM_ADMIN)) return 'PlatformAdmin';
  if (userRoles.includes(ROLES.TENANT_ADMIN)) return 'TenantAdmin';
  return 'Analyst';
}

// Role hierarchy: PlatformAdmin > TenantAdmin > Analyst
const ROLE_HIERARCHY: Record<string, number> = {
  [ROLES.PLATFORM_ADMIN]: 3,
  [ROLES.TENANT_ADMIN]: 2,
  [ROLES.ANALYST]: 1,
};

// Check if user has required role (or a higher role in the hierarchy)
export const hasRequiredRole = (userRoles: string[], requiredRole: UserRole): boolean => {
  const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;
  return userRoles.some(role => (ROLE_HIERARCHY[role] || 0) >= requiredLevel);
};